import { CalendarDays, CalendarX2, Megaphone } from 'lucide-react';
import type { ConversationDetail } from '../api/conversations';
import { formatDayInZone } from '../lib/datetime';

/**
 * The link from a thread back to the event or announcement it belongs to.
 *
 * **The link only ran one way before this.** An event screen opens its thread; the thread knew only
 * that it was anchored to *something*, because `Conversation.Anchor` records the kind of parent and
 * deliberately not the parent's id — the link is the parent's, and a second copy of it on the
 * conversation is what §1b/TROOP-39 refused. So the reverse direction is a lookup, and this is what
 * it is for.
 *
 * **Why not pass it in navigation state:** a thread is reachable by URL — from the conversations
 * page, from a notification, from a bookmark — and a link that only appears when you arrived from
 * the event is missing exactly when somebody needs it.
 *
 * One component for both doors, like `ParticipantStrip`: what differs is where the link points, and
 * that is the caller's to decide.
 */
export function AnchorLink({
  detail,
  onOpen,
}: {
  detail: ConversationDetail | null;
  /** Where this reader's copy of the parent lives — `/units/…` for a member, `/family/…` for a parent. */
  onOpen: (kind: string, id: string) => void;
}) {
  const anchor = detail?.anchoredTo;

  if (!anchor) {
    return null;
  }

  const isEvent = anchor.kind === 'Event';
  const Icon = anchor.cancelled ? CalendarX2 : isEvent ? CalendarDays : Megaphone;

  return (
    <button
      type="button"
      onClick={() => onOpen(anchor.kind, anchor.id)}
      className="flex w-full items-center gap-2 text-left"
      style={{
        background: 'none',
        border: 'none',
        padding: 'var(--space-3) 0',
        minHeight: 'var(--tap-min)',
        color: 'var(--color-text-secondary)',
      }}
    >
      <Icon
        size={15}
        aria-hidden="true"
        style={{
          flexShrink: 0,
          color: anchor.cancelled ? 'var(--color-status-blocked)' : 'var(--color-text-muted)',
        }}
      />

      <span className="t-caption" style={{ textDecoration: 'underline' }}>
        {isEvent ? 'About' : 'On'} {anchor.title}
        {anchor.startsAt && (
          <>
            {' '}
            &middot;{' '}
            <span className="tabular">
              {formatDayInZone(anchor.startsAt, Intl.DateTimeFormat().resolvedOptions().timeZone)}
            </span>
          </>
        )}
      </span>

      {anchor.cancelled && (
        // A thread outliving its event's cancellation is ordinary, and the families still in it are
        // exactly the people who need to know. Said on the link rather than left to the event page.
        <span className="t-caption" style={{ color: 'var(--color-status-blocked)', flexShrink: 0 }}>
          Cancelled
        </span>
      )}
    </button>
  );
}
